import { facebookSignIn } from 'actions/authentication/authentication';
import { trackUserLoginSignup } from 'actions/tracker/tracker';
import {
  FACEBOOK_APP_ID_DEV,
  FACEBOOK_APP_ID_PROD,
  PROD_DOMAINS_FOR_SOCIAL_SIGN_IN,
} from 'constants/config';
import {
  UserLoginSource,
  UserLoginType,
} from 'contexts/LoginContext/LoginContext';
import { useLoginOrigin } from 'hooks/useLoginOrigin/useLoginOrigin';
import { useEffect, useState } from 'react';
import { useDispatch } from 'store/configureStore';
import { useTranslations } from 'utils/translations/useTranslations';
import { url } from 'utils/url/url';
import { ReactComponent as FacebookIcon } from './assets/facebookIcon.svg';
import { initialize } from './facebookSDK/initialize';
import { useFacebookLogin } from './hooks/useFacebookLogin/useFacebookLogin';

const getAppId = () => {
  const domainName = url.getDomainName();
  return PROD_DOMAINS_FOR_SOCIAL_SIGN_IN.includes(domainName)
    ? FACEBOOK_APP_ID_PROD
    : FACEBOOK_APP_ID_DEV;
};

export function FacebookSignIn({
  isLogin,
  onClick,
  onSuccess,
  onError,
  captchaToken,
}: {
  isLogin: boolean;
  onClick: (handleProviderSignIn: () => void) => void;
  onSuccess?: () => void;
  onError: (err: unknown) => void;
  captchaToken?: string;
}) {
  const [sdkReady, setSdkReady] = useState(false);

  useEffect(() => {
    const appId = getAppId();

    initialize({ appId }).then(() => setSdkReady(true));
  }, []);

  return (
    <FacebookSignInButton
      isLogin={isLogin}
      onClick={onClick}
      onError={onError}
      onSuccess={onSuccess}
      sdkReady={sdkReady}
      captchaToken={captchaToken}
    />
  );
}

function FacebookSignInButton({
  isLogin,
  onClick,
  onSuccess,
  onError,
  sdkReady,
  captchaToken,
}: {
  isLogin: boolean;
  onClick: (handleProviderSignIn: () => void) => void;
  onSuccess?: () => void;
  onError: (err: unknown) => void;
  sdkReady: boolean;
  captchaToken?: string;
}) {
  const i18n = useTranslations();
  const dispatch = useDispatch();
  const { loginOrigin } = useLoginOrigin();

  const handleResponse = async (authCode: string) => {
    try {
      await dispatch(
        facebookSignIn({
          authCode,
          captchaToken,
        }),
      );
      onSuccess?.();
      dispatch(
        trackUserLoginSignup(
          isLogin ? UserLoginType.LOGIN : UserLoginType.SIGNUP,
          UserLoginSource.FACEBOOK,
          loginOrigin,
        ),
      );
    } catch (error) {
      onError?.(error);
    }
  };

  const handleFacebookSignIn = useFacebookLogin({
    onSuccess: authCode => {
      handleResponse(authCode);
    },
    onError: error => {
      onError(error);
    },
  });

  return (
    <button
      className={`
        mb-4 flex w-full cursor-pointer flex-row items-center justify-center gap-2
        rounded-md border border-gray-300
        bg-white px-6 py-2.5 text-blue-600 shadow-[0_1px_1px_rgba(0,0,0,0.08)]
        disabled:opacity-50
      `}
      onClick={() => onClick(handleFacebookSignIn)}
      data-testid="facebookSignInButton"
      type="submit"
      disabled={!sdkReady}
    >
      <FacebookIcon className="w-6" />
      <span className="text-base text-gray-950">
        {i18n.t(
          isLogin
            ? 'userLogin.social.facebook.login'
            : 'userLogin.social.facebook.signup',
        )}
      </span>
    </button>
  );
}
